import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import { ROUTE_TEMPLATES } from 'CorePackage/MainModule/routes/routes';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';

import styles from './PlanHeader.module.scss';
import { PlanHeaderButtons } from './PlanHeaderButtons';
import { getPlanWatchOnlyByTestUser } from '../selectors/planSelectors';

interface IPlanHeaderProps {
  plan?: IKnmPlan;
  handleOpenSignModal: () => void;
  handleUploadButtonClick: () => void;
  handleOpenSignInfoModal: () => void;
  handleAddKnmButtonClick: () => void;
  handleOpenSignUploadModal: () => void;
  handleOpenChangeKnoModal: () => void;
  isWatchOnlyByTestUser?: boolean;
}

const PlanHeaderWithoutStore = ({
  plan,
  handleOpenSignModal,
  handleUploadButtonClick,
  handleOpenSignInfoModal,
  handleAddKnmButtonClick,
  handleOpenSignUploadModal,
  handleOpenChangeKnoModal,
  isWatchOnlyByTestUser,
}: IPlanHeaderProps) => {
  const createdInfoText = useMemo(
    () =>
      plan?.created &&
      `Создано${plan?.createdBy ? ` ${plan?.createdBy.fullName} (${plan?.createdBy.id})` : ''} ${formatDate({
        date: new Date(plan?.created).toISOString(),
        dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
        parseFormat: DATE_FORMAT_ISO_FNS,
      })}. `,
    [plan?.created, plan?.createdBy],
  );

  const updatedInfoText = useMemo(
    () =>
      plan?.updated
        ? `Обновлено${plan?.updatedBy ? ` ${plan?.updatedBy.fullName} (${plan?.updatedBy.id})` : ''} ${formatDate({
            date: new Date(plan?.updated).toISOString(),
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_ISO_FNS,
          })}. `
        : '',
    [plan?.updated, plan?.updatedBy],
  );

  const deletedInfoText = useMemo(
    () =>
      plan?.updated && plan?.deleted
        ? `Удалено${plan?.updatedBy ? ` ${plan?.updatedBy.fullName} (${plan?.updatedBy.id})` : ''} ${formatDate({
            date: new Date(plan?.updated).toISOString(),
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_ISO_FNS,
          })}. `
        : '',
    [plan?.deleted, plan?.updated, plan?.updatedBy],
  );

  const backUrl: string | null = useMemo(() => {
    const params = new URLSearchParams(window.location.search);

    return params.get('backUrl');
  }, []);

  return (
    <div className={styles.Header}>
      <div className={styles.HeaderContent}>
        <Link className={styles.BackButton} to={backUrl || ROUTE_TEMPLATES}>
          <span className={styles.BackButtonIcon} />
        </Link>
        <div className={styles.TitleBlock}>
          <h3 className={styles.Title}>
            {plan?.isTest && <span className={styles.TestUserWrapper}>ТЕСТ</span>}
            План № {plan?.id}&nbsp;
            <span className={styles.TitleYear}>
              (План на {plan?.startDate && new Date(plan?.startDate).getFullYear()} год)
            </span>
          </h3>
          &nbsp;<span className={styles.Status}>{plan?.erknmStatus || plan?.status}</span>
          <div className={styles.PublishedStatus}>
            <span>Статус подписания: &nbsp;</span>
            <span className={styles.Status}>
              {plan?.knmPlanData?.signedXlsxFile?.signed ? 'Подписан' : 'Не подписан'}
            </span>
            <Tooltip
              placement="right"
              content={
                <div className={styles.TooltipContent}>
                  <div className={styles.TooltipHeader}>Вы можете подписать документ двумя способами:</div>
                  <ul>
                    <li>ПОДПИСАТЬ ЧЕРЕЗ БРАУЗЕР (используйте соответсвующий пункт в меню кнопки Действия)</li>
                    <li>
                      ПОДПИСАТЬ ОТКРЕПЛЕННО (в меню Действия выберите «Экспорт плана в XLSX», выгрузите все КНМ в
                      формате XLM, подпишите документы и сформируйте файл SIG, загрузите файл SIG через пункт меню
                      «Загрузить подпись»)
                    </li>
                  </ul>
                </div>
              }
            >
              <span className={styles.TooltipIcon} />
            </Tooltip>
          </div>
        </div>
        {!isWatchOnlyByTestUser && (
          <PlanHeaderButtons
            handleOpenSignModal={handleOpenSignModal}
            handleUploadButtonClick={handleUploadButtonClick}
            handleOpenSignInfoModal={handleOpenSignInfoModal}
            handleAddKnmButtonClick={handleAddKnmButtonClick}
            handleOpenSignUploadModal={handleOpenSignUploadModal}
            handleOpenChangeKnoModal={handleOpenChangeKnoModal}
          />
        )}
      </div>
      <div className={styles.InfoBlock}>
        <div className={styles.ActionInfo}>
          {createdInfoText}
          {updatedInfoText}
          {deletedInfoText}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  plan: state.plan.data,
  isWatchOnlyByTestUser: getPlanWatchOnlyByTestUser(state),
});

export const PlanHeader = connect(mapStateToProps, null)(PlanHeaderWithoutStore);
